@if (!settingsReady) {
  <p role="alert">Sitewide settings, page choices, or placement options are unavailable. Reload this page before editing Sitewide Unlock.</p>
}
@if (form.controls.saveError.value) {
  <p class="field-error" role="alert">{{ form.controls.saveError.value }}</p>
}
<form [formGroup]="form" autoTrackUnsavedChanges>
  <section class="setting-section" aria-labelledby="siteWideUnlockEnabled-label">
    <div class="setting-row">
      <h3 id="siteWideUnlockEnabled-label"><label for="siteWideUnlockEnabled">Sitewide Unlock</label></h3>
      <div class="toggle">
        <p-toggleswitch inputId="siteWideUnlockEnabled" formControlName="siteWideUnlockEnabled" ariaLabel="Sitewide Unlock"></p-toggleswitch>
        <span aria-hidden="true">@if (form.controls.siteWideUnlockEnabled.value) { On } @else { Off }</span>
      </div>
    </div>
    <p id="siteWideUnlockEnabled-description">Let shoppers unlock private offers across eligible website pages.</p>
  </section>
  <section class="setting-section" formGroupName="presentation" aria-labelledby="sharedPreferences-label">
    <h3 id="sharedPreferences-label">Colors &amp; shared button text</h3>
    <div class="color-fields">
      <div class="presentation-field">
        <label for="siteWidePrimaryHex">Primary unlock color</label>
        <div class="color-inputs">
          <input #primaryPicker type="color" [value]="form.controls.presentation.controls.primaryColor.value"
                 [disabled]="form.controls.presentation.controls.primaryColor.disabled"
                 (input)="setColor('primaryColor', primaryPicker.value)" aria-label="Primary unlock color picker">
          <input id="siteWidePrimaryHex" type="text" formControlName="primaryColor" aria-describedby="primaryColor-help">
        </div>
        <p id="primaryColor-help">Banner background; popup and form CTA; post-unlock icon and border.</p>
        @if (form.controls.presentation.controls.primaryColor.invalid) {
          <p role="alert">Enter a six-digit hex color, such as #16a34a.</p>
        }
      </div>
      <div class="presentation-field">
        <label for="siteWideSecondaryHex">Secondary color</label>
        <div class="color-inputs">
          <input #secondaryPicker type="color" [value]="form.controls.presentation.controls.secondaryColor.value"
                 [disabled]="form.controls.presentation.controls.secondaryColor.disabled"
                 (input)="setColor('secondaryColor', secondaryPicker.value)" aria-label="Secondary color picker">
          <input id="siteWideSecondaryHex" type="text" formControlName="secondaryColor" aria-describedby="secondaryColor-help">
        </div>
        <p id="secondaryColor-help">Banner CTA and post-unlock button backgrounds.</p>
        @if (form.controls.presentation.controls.secondaryColor.invalid) {
          <p role="alert">Enter a six-digit hex color, such as #002c4c.</p>
        }
      </div>
    </div>
    <ng-container [ngTemplateOutlet]="textInputs" [ngTemplateOutletContext]="{fields: sharedFields}"></ng-container>
    <p class="field-help">Used by the banner, popup, and unlock-form buttons. All three match.</p>
    <p>Text is white on colored backgrounds and black on white backgrounds.</p>
  </section>
  <section class="setting-section" aria-labelledby="unlockForm-label">
    <h3 id="unlockForm-label">Unlock form</h3>
    <ng-container [ngTemplateOutlet]="textInputs" [ngTemplateOutletContext]="{fields: formFields}"></ng-container>
    <p class="field-help">Button text uses the shared CTA label above. Disclosure is inherited from Private Offers settings.</p>
  </section>
  <section class="setting-section" aria-labelledby="bannerEnabled-label">
    <div class="setting-row">
      <h3 id="bannerEnabled-label"><label for="bannerEnabled">Unlock banner</label></h3>
      <div class="toggle">
        <p-toggleswitch inputId="bannerEnabled" formControlName="bannerEnabled" ariaLabel="Unlock banner"></p-toggleswitch>
        <span aria-hidden="true">@if (form.controls.bannerEnabled.value) { On } @else { Off }</span>
      </div>
    </div>
    <ng-container [ngTemplateOutlet]="textInputs" [ngTemplateOutletContext]="{fields: bannerFields}"></ng-container>
  </section>
  <section class="setting-section" aria-labelledby="unlockPopupEnabled-label">
    <div class="setting-row">
      <h3 id="unlockPopupEnabled-label"><label for="unlockPopupEnabled">Pre-unlock pop-up teaser</label></h3>
      <div class="toggle">
        <p-toggleswitch inputId="unlockPopupEnabled" formControlName="unlockPopupEnabled" ariaLabel="Pre-unlock pop-up teaser"></p-toggleswitch>
        <span aria-hidden="true">@if (form.controls.unlockPopupEnabled.value) { On } @else { Off }</span>
      </div>
    </div>
    <p id="unlockPopupEnabled-description">Displays only before unlocking.</p>
    <div class="placements">
      <div class="placement">
        <label for="unlockDesktop">Desktop placement<span class="visually-hidden"> for pre-unlock pop-up teaser</span></label>
        <p-select inputId="unlockDesktop" formControlName="unlockDesktop" [options]="desktopOptions" placeholder="Select a desktop position"
                  optionLabel="label" optionValue="id" appendTo="body" [style]="{width: '100%'}"></p-select>
      </div>
    </div>
    <p class="placement-note">On mobile, the teaser appears at the bottom center.</p>
    <ng-container [ngTemplateOutlet]="textInputs" [ngTemplateOutletContext]="{fields: popupFields}"></ng-container>
  </section>
  <section class="setting-section" aria-labelledby="navigationPopupEnabled-label">
    <div class="setting-row">
      <h3 id="navigationPopupEnabled-label"><label for="navigationPopupEnabled">Post-unlock navigation</label></h3>
      <div class="toggle">
        <p-toggleswitch inputId="navigationPopupEnabled" formControlName="navigationPopupEnabled" ariaLabel="Post-unlock navigation"></p-toggleswitch>
        <span aria-hidden="true">@if (form.controls.navigationPopupEnabled.value) { On } @else { Off }</span>
      </div>
    </div>
    <p id="navigationPopupEnabled-description">Displays only after unlocking.</p>
    <div class="placements">
      <div class="placement">
        <label for="navigationDesktop">Desktop placement<span class="visually-hidden"> for post-unlock navigation</span></label>
        <p-select inputId="navigationDesktop" formControlName="navigationDesktop" [options]="desktopOptions" placeholder="Select a desktop position"
                  optionLabel="label" optionValue="id" appendTo="body" [style]="{width: '100%'}"></p-select>
      </div>
    </div>
    <p class="placement-note">On mobile, navigation appears at the bottom right.</p>
    <ng-container [ngTemplateOutlet]="textInputs" [ngTemplateOutletContext]="{fields: navigationFields}"></ng-container>
    <div class="destination-field" formGroupName="presentation">
      <h4>Navigation destination</h4>
      <label class="inheritance-option">
        <input #inheritDestination type="checkbox" [checked]="useInheritedDestination"
               [disabled]="form.controls.presentation.controls.destinationUrlOverride.disabled"
               (change)="setDestinationInheritance(inheritDestination.checked)">
        Use new leaderboard URL from Account &gt; Website
      </label>
      @if (destinationError; as message) {
        <p class="destination-error" role="alert">{{ message }}</p>
      }
      <label for="siteWideDestinationOverride">Destination URL</label>
      @if (useInheritedDestination) {
        <input id="siteWideDestinationOverride" class="inherited-destination" type="text" [value]="defaultDestinationUrl"
               readonly [disabled]="form.controls.presentation.controls.destinationUrlOverride.disabled" placeholder="Not configured"
               aria-describedby="siteWideDestination-help">
      } @else {
        <input id="siteWideDestinationOverride" type="text" formControlName="destinationUrlOverride"
               placeholder="/offers or an HTTP(S) URL on the account website" aria-describedby="siteWideDestination-help">
      }
      <p id="siteWideDestination-help">Follows the account's leaderboard URL. Uncheck to set a same-hostname override.</p>
      <p>Navigation is hidden on this destination page, even when the page is included in widget targeting.</p>
      @if (form.controls.presentation.controls.destinationUrlOverride.hasError('characterLimit')) {
        <p role="alert">Shorten the URL to {{ urlLimit }} characters or fewer.</p>
      }
    </div>
  </section>
  <section class="setting-section" aria-labelledby="page-types-heading">
    <h3 id="page-types-heading">Page types</h3>
    <p id="page-types-help">Choose where all enabled Sitewide Unlock widgets can appear. These choices are saved even while Sitewide Unlock is off.</p>
    <div class="page-type-options" formGroupName="pageTypes">
      @for (option of pageTypeOptions; track option.key) {
        <label class="page-choice" [for]="'sitewide-page-' + option.key">
          <input type="checkbox" [id]="'sitewide-page-' + option.key" [formControlName]="option.key" aria-describedby="page-types-help">
          <span>{{ option.label }}</span>
        </label>
      }
    </div>
  </section>
  <section class="setting-section" aria-labelledby="custom-paths-heading">
    <div class="setting-row">
      <h3 id="custom-paths-heading">Custom paths</h3>
      <button type="button" class="path-button" (click)="addCustomPath()"
              [disabled]="form.controls.pageTypes.disabled || changedPathCount() >= maximumPathChanges">Add path</button>
    </div>
    <p id="custom-paths-help">Select an existing path or add a path or URL on this account’s website.
      A selected custom path can include a page even when its page type is off.
      Unchecking removes that custom inclusion; an enabled page type may still allow the widgets.</p>
    <p id="custom-pattern-help" class="placement-note">Use <code>*</code> for zero or more characters, including slashes.
      <code>/finance</code> matches one path; <code>/finance/*</code> matches paths below it;
      <code>/finance*</code> also matches <code>/finance</code> and other paths starting with that text.
      Do not include query strings or fragments. Saving trims and normalizes slashes.</p>
    <p class="placement-note">Editing a selected path replaces its inclusion. All paths use the same widget and placement settings.
      Post-unlock navigation stays hidden on its destination page.</p>
    @if (settingsReady && form.controls.customPaths.length === 0) {
      <p class="placement-note">No custom paths are configured for this account.</p>
    }
    <div class="custom-paths" formArrayName="customPaths">
      @for (row of form.controls.customPaths.controls; track row; let index = $index) {
        <div class="custom-path-row" [formGroupName]="index">
          <div class="custom-path-fields">
            @if (row.controls.saved.value !== null) {
              <label class="page-choice" [for]="'custom-included-' + index">
                <input type="checkbox" [id]="'custom-included-' + index" formControlName="selected"
                       [attr.aria-label]="'Include custom path ' + row.controls.saved.value.path"
                       aria-describedby="custom-paths-help" (change)="toggleCustomPath(row)">
                <span>Include</span>
              </label>
            } @else {
              <span class="new-path-label">New inclusion</span>
            }
            <div class="custom-path-input">
              <label [for]="'custom-path-' + index">Path or pattern {{ index + 1 }}</label>
              <input type="text" [id]="'custom-path-' + index" formControlName="path" placeholder="/finance*"
                     [attr.aria-describedby]="'custom-pattern-help custom-path-error-' + index"
                     [attr.aria-invalid]="row.hasError('pathInput') && row.controls.path.touched"
                     autocomplete="off" spellcheck="false">
            </div>
            @if (row.controls.saved.value === null) {
              <button type="button" class="path-button" (click)="removeNewPath(row)"
                      [disabled]="form.controls.pageTypes.disabled" [attr.aria-label]="'Remove new path ' + (index + 1)">Remove</button>
            }
          </div>
          <div [id]="'custom-path-error-' + index" class="field-error" aria-live="polite">
            @if (row.hasError('pathInput') && (row.controls.path.touched || row.controls.path.dirty)) {
              <p>{{ row.getError('pathInput') }}</p>
            }
            @if (row.controls.saveError.value) {
              <p>{{ row.controls.saveError.value }}</p>
            }
          </div>
        </div>
      }
    </div>
    @if (form.hasError('tooManyPaths')) {
      <p class="field-error" role="alert">Save no more than {{ maximumPathChanges }} custom-path changes at a time.</p>
    }
  </section>
  @if (settingsReady && form.hasError('siteWidePlacement')) {
    <p class="placement-note" role="alert">Select a valid desktop position for each enabled pop-up before saving.</p>
  }
</form>
<p class="placement-note">The pre-unlock pop-up teaser and post-unlock navigation never display at the same time, so they will not overlap each other—even if you select the same position.</p>

<ng-template #textInputs let-fields="fields">
  <div [formGroup]="form.controls.presentation">
    @for (field of fields; track field.name) {
      <div class="presentation-field text-field">
        <label [for]="'siteWide-' + field.name">{{ field.label }}</label>
        @if (field.name === 'bannerBody' || field.name === 'popupBody' || field.name === 'formSubtitle') {
          <textarea [id]="'siteWide-' + field.name" [formControlName]="field.name" rows="3"
                    [attr.aria-describedby]="'siteWide-' + field.name + '-error'"></textarea>
        } @else {
          <input [id]="'siteWide-' + field.name" type="text" [formControlName]="field.name"
                 [attr.aria-describedby]="'siteWide-' + field.name + '-error'">
        }
        @if (form.controls.presentation.controls[field.name].invalid) {
          <p [id]="'siteWide-' + field.name + '-error'" role="alert">Shorten this text to {{ field.limit }} characters or fewer.</p>
        }
      </div>
    }
  </div>
</ng-template>
